import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  sendMessage,
  fetchConversation,
  fetchConversations,
  addMessage,
  setTyping,
  clearMessages,
  markMessagesAsRead,
  revokeMessage
} from '../../store/slices/messagesSlice';
import { Search, PlusCircle, FileText, MoreVertical, Paperclip, Image, Smile, Send, MessageSquare } from 'lucide-react';
import MessageSkeleton from '../../components/MessageSkeleton';
import MessageWarning from '../../components/MessageWarning';
import socket from '../../services/socket';
import api from '../../services/api';
import { OnlineStatusBadge } from '../../utils/onlineStatus';
import CustomOfferCard from '../../components/CustomOfferCard';
import CustomRequestCard from '../../components/CustomRequestCard';
import CustomOfferModal from '../../components/CustomOfferModal';
import CustomRequestModal from '../../components/CustomRequestModal';
import ChatOrderModal from '../../components/ChatOrderModal';
import VerificationRequiredModal from '../../components/verification/VerificationRequiredModal';
import { isSecondaryVerificationError } from '../../utils/verificationGate';
import ChatOrderCard from '../../components/ChatOrderCard';
import StructuredUpdateCard from '../../components/StructuredUpdateCard';
import QuickReplyButton from '../../components/QuickReplyButton';
import VoiceNoteRecorder from '../../components/VoiceNoteRecorder';
import VoiceMessagePlayer from '../../components/VoiceMessagePlayer';
import RevokedMessageNotice from '../../components/RevokedMessageNotice';
import { toast } from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────────────────
// Client-side anti-poaching scanner (matches backend enforcement in messageController.js)
// Provides real-time UX feedback; server is the authoritative enforcer.
// ─────────────────────────────────────────────────────────────────────────────
const CHAT_BLOCKED_PATTERNS = [
  { type: 'phone',   pattern: /(\+233|0)[0-9]{9}/g,           message: 'Phone numbers are not allowed in messages' },
  { type: 'phone',   pattern: /\b\d{10,15}\b/g,               message: 'Phone numbers are not allowed in messages' },
  { type: 'phone',   pattern: /\b(\d[\s.-]{0,2}){9,14}\d\b/g, message: 'Digit sequences that look like phone numbers are not allowed' },
  { type: 'email',   pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, message: 'Email addresses are not allowed in messages' },
  { type: 'email',   pattern: /[a-zA-Z0-9._%+-]+\s*\[\s*at\s*\]\s*[a-zA-Z0-9.-]+/gi, message: 'Obfuscated email is not allowed' },
  { type: 'email',   pattern: /[a-zA-Z0-9._%+-]+\s*\(\s*at\s*\)\s*[a-zA-Z0-9.-]+/gi, message: 'Obfuscated email is not allowed' },
  { type: 'social',  pattern: /@[a-zA-Z0-9_.]{3,}/g,          message: 'Social media handles are not allowed' },
  { type: 'social',  pattern: /whatsapp/gi,                   message: 'WhatsApp references are not allowed. Keep all chats on GigShip.' },
  { type: 'social',  pattern: /telegram/gi,                   message: 'Telegram references are not allowed. Keep all chats on GigShip.' },
  { type: 'social',  pattern: /snapchat/gi,                   message: 'Snapchat references are not allowed' },
  { type: 'social',  pattern: /instagram/gi,                  message: 'Instagram references are not allowed' },
  { type: 'social',  pattern: /tiktok/gi,                     message: 'TikTok references are not allowed' },
  { type: 'social',  pattern: /facebook\.com/gi,              message: 'Facebook links are not allowed' },
  { type: 'social',  pattern: /wh[@4]ts[@4]pp/gi,             message: 'WhatsApp references are not allowed' },
  { type: 'social',  pattern: /t[e3]l[e3]gr[a4]m/gi,         message: 'Telegram references are not allowed' },
  { type: 'social',  pattern: /inst[@4]gr[@4]m/gi,            message: 'Instagram references are not allowed' },
  { type: 'payment', pattern: /paypal/gi,                     message: 'External payment references are not allowed' },
  { type: 'payment', pattern: /venmo/gi,                      message: 'External payment references are not allowed' },
  { type: 'payment', pattern: /cashapp|cash\s*app/gi,         message: 'External payment references are not allowed' },
  { type: 'payment', pattern: /\bmomo\b/gi,                   message: 'MoMo payment references are not allowed. Use GigShip wallet.' },
  { type: 'payment', pattern: /mpesa/gi,                      message: 'M-Pesa references are not allowed. Use GigShip wallet.' },
  { type: 'bypass',  pattern: /call\s*me/gi,                  message: 'Please keep all communication within GigShip' },
  { type: 'bypass',  pattern: /text\s*me/gi,                  message: 'Please keep all communication within GigShip' },
  { type: 'bypass',  pattern: /dm\s*me/gi,                    message: 'Please keep all communication within GigShip' },
  { type: 'bypass',  pattern: /off\s*platform|off-platform/gi, message: 'Off-platform transactions violate GigShip terms' },
  { type: 'bypass',  pattern: /outside\s*of\s*gigship/gi,     message: 'Off-platform transactions violate GigShip terms' },
  { type: 'bypass',  pattern: /contact\s*me\s*(on|via|at|through)/gi, message: 'Please keep all communication within GigShip' },
];

function detectChatViolations(text) {
  if (!text || typeof text !== 'string') return [];
  const violations = [];
  for (const { type, pattern, message } of CHAT_BLOCKED_PATTERNS) {
    pattern.lastIndex = 0;
    if (pattern.test(text)) {
      violations.push({ type, message });
    }
  }
  return violations;
}

const Messages = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth);
  const {
    conversations,
    messages,
    loading,
    typing,
    onlineUsers
  } = useSelector((state) => state.messages);

  const [messageText, setMessageText] = useState('');
  const [contentViolations, setContentViolations] = useState([]);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showChatOrderModal, setShowChatOrderModal] = useState(false);
  const [isNegotiatingOrder, setIsNegotiatingOrder] = useState(false);
  const [activeChatOrderForNegotiation, setActiveChatOrderForNegotiation] = useState(null);
  const [activeRequestForCounter, setActiveRequestForCounter] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [recorderMode, setRecorderMode] = useState('idle');
  const messagesEndRef = useRef(null);
  const [newChatUser, setNewChatUser] = useState(null);
  // ?new=<userId> waits for the conversation list so an existing chat with that user is reused
  const [conversationsReady, setConversationsReady] = useState(false);

  const conversationId = searchParams.get('conversation');
  const targetUserId = searchParams.get('new');

  useEffect(() => {
    if (targetUserId && conversationsReady) {
      // ?new= carries a user id: select the listed direct conversation whose other participant is that user
      const existingConv = conversations.find(c =>
        c.participants?.length === 2 &&
        c.participants.some(p => (p.id || p._id) === targetUserId)
      );
      if (existingConv) {
        setSearchParams({ conversation: existingConv.id || existingConv._id }, { replace: true });
      } else if (!newChatUser || (newChatUser.id || newChatUser._id) !== targetUserId) {
        api.get(`/users/${targetUserId}`).then(res => {
          if (res.data.success) {
             setNewChatUser(res.data.data.user || res.data.data);
             // Ensure conversationId picks this up by pretending it's the conversationId
             setSearchParams({ conversation: targetUserId, new: targetUserId }, { replace: true });
          }
        }).catch(err => console.error("Could not fetch target user"));
      }
    }
  }, [targetUserId, conversations, conversationsReady, newChatUser, setSearchParams]);

  // Derived state
  const rawConversation = conversations.find(c => (c.id || c._id) === conversationId);
  const otherParticipant = rawConversation?.participants?.find(p => (p.id || p._id) !== (user?.id || user?._id)) 
    || (conversationId === targetUserId ? newChatUser : null);

  // A brand-new chat (opened from ?new=) has no Conversation yet: its "conversationId" is the other
  // user's id, so it must not be sent to the API as a conversation id
  const realConversationId = rawConversation ? (rawConversation.id || rawConversation._id) : undefined;

  // After an offer, request or order proposal is sent: reload the open chat; in a brand-new chat the
  // API has now created the conversation, so the list is reloaded too and ?new= switches to it
  const refreshAfterCardSent = () => {
    dispatch(fetchConversation({ conversationId }));
    if (!realConversationId) dispatch(fetchConversations());
  };

  const selectedConversation = rawConversation ? {
    conversationId: rawConversation.id || rawConversation._id,
    userId: otherParticipant?.id || otherParticipant?._id,
    ...rawConversation
  } : (conversationId === targetUserId && newChatUser) ? {
    conversationId: targetUserId, // Will just be used as frontend reference
    userId: targetUserId,
    participants: [user, newChatUser],
    messages: []
  } : null;

  const recipientOnlineStatus = {
    isOnline: otherParticipant ? onlineUsers.includes(otherParticipant.id || otherParticipant._id) : false,
    lastSeen: otherParticipant?.lastSeen
  };

  // The order buttons follow the mode the user is in (activeRole, switched in Settings): Gigger mode
  // proposes orders ("Create Order"), Shipper mode — the default — requests them ("Request Order")
  const isGigger = user?.activeRole === 'gigger';
  const isShipper = !isGigger;

  useEffect(() => {
    let active = true;
    Promise.resolve(dispatch(fetchConversations())).finally(() => {
      if (active) setConversationsReady(true);
    });
    return () => { active = false; };
  }, [dispatch]);

  useEffect(() => {
    if (conversationId) {
      dispatch(fetchConversation({ conversationId }));
      dispatch(markMessagesAsRead({ conversationId }));

      // Join room
      socket.emit('join_conversation', conversationId);

      // Listen for chat-order updates in real-time
      const handleChatOrderUpdate = () => {
        dispatch(fetchConversation({ conversationId }));
      };
      socket.on('chat_order_update', handleChatOrderUpdate);
      socket.on('new_chat_order', handleChatOrderUpdate);

      // Handle message_revoked socket event
      const handleMessageRevoked = (payload) => {
        if (!payload?.messageId) return;
        dispatch(revokeMessage(payload));
      };
      socket.on('message_revoked', handleMessageRevoked);

      return () => {
        socket.emit('leave_conversation', conversationId);
        socket.off('chat_order_update', handleChatOrderUpdate);
        socket.off('new_chat_order', handleChatOrderUpdate);
        socket.off('message_revoked', handleMessageRevoked);
        dispatch(clearMessages());
      };
    }
  }, [dispatch, conversationId]);

  useEffect(() => {
    // Scroll to bottom
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !conversationId) return;

    // Client-side content guard — server enforces this too, but UX benefit to block early
    const violations = detectChatViolations(messageText);
    if (violations.length > 0) {
      setContentViolations(violations);
      toast.error('Your message contains prohibited content. Please remove it before sending.');
      return;
    }

    const messageData = {
      conversationId,
      content: { text: messageText, type: 'text' },
      recipientId: selectedConversation.userId
    };

    const result = await dispatch(sendMessage(messageData));
    if (sendMessage.fulfilled.match(result)) {
      setMessageText('');
      setContentViolations([]);
    } else {
      // Surface backend rejection (e.g. content filter 400 response)
      toast.error(result.payload || 'Failed to send message');
    }
  };

  const handleVoiceSent = (voiceData) => {
    dispatch(addMessage({
      _id: 'voice-' + Date.now(),
      sender: user,
      senderId: user?.id || user?._id,
      conversationId: voiceData.conversationId,
      contentType: 'voice',
      audioUrl: voiceData.audioUrl,
      duration: voiceData.duration,
      content: {
        type: 'voice',
        url: voiceData.audioUrl,
        duration: voiceData.duration,
        text: '[Voice Message]'
      },
      createdAt: voiceData.createdAt || new Date().toISOString()
    }));
  };

  const handleTyping = (e) => {
    const newText = e.target.value;
    setMessageText(newText);
    // Real-time content scanning for immediate UX feedback
    setContentViolations(detectChatViolations(newText));
    if (conversationId) {
      socket.emit('typing', { conversationId, userId: user.id || user._id, isTyping: true });
    }
  };

  const handleOfferUpdate = () => {
    if (conversationId) dispatch(fetchConversation({ conversationId }));
  };

  const handleRequestUpdate = () => {
    if (conversationId) dispatch(fetchConversation({ conversationId }));
  };

  const handleCounterRequest = (request) => {
    setActiveRequestForCounter(request);
    setShowOfferModal(true);
  };

  const handleChatOrderAccept = async (order) => {
    try {
      await api.patch(`/chat-orders/${order.id || order._id}/respond`, { action: 'accept' });
      toast.success('Proposal accepted and paid!');
      dispatch(fetchConversation({ conversationId }));
    } catch (error) {
      // No client-side pre-check: the Redux auth user carries no verificationLevel, so the server's 403 decides.
      if (isSecondaryVerificationError(error)) {
        setShowVerificationModal(true);
      } else {
        toast.error(error.response?.data?.message || 'Failed to accept proposal');
      }
    }
  };

  const handleChatOrderNegotiate = (order) => {
    setActiveChatOrderForNegotiation(order);
    setIsNegotiatingOrder(true);
    setShowChatOrderModal(true);
  };

  useEffect(() => {
    const autoOpenOrder = searchParams.get('autoOpenOrder');
    const portfolioId = searchParams.get('portfolioId');

    if (autoOpenOrder === 'true' && conversationId && !showChatOrderModal) {
      // Set some context for the new order
      setShowChatOrderModal(true);

      // Clean up search params so it doesn't reopen on refresh
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('autoOpenOrder');
      setSearchParams(newParams, { replace: true });
    }
  }, [conversationId, searchParams]);

  const handleChatOrderReject = async (order) => {
    try {
      await api.patch(`/chat-orders/${order.id || order._id}/respond`, { action: 'reject' });
      toast.success('Proposal rejected');
      dispatch(fetchConversation({ conversationId }));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject proposal');
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)] bg-white">
      {/* Sidebar */}
      <div className="w-1/3 border-r flex flex-col hidden md:flex">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold mb-4">{t('pages.messages.title')}</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('pages.messages.search_placeholder')}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && conversations.length === 0 ? (
            <>
              <MessageSkeleton />
              <MessageSkeleton />
              <MessageSkeleton />
            </>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>{t('pages.messages.no_conversations')}</p>
            </div>
          ) : (
            conversations.map((conversation) => {
              const participant = conversation.participants?.find(p => (p.id || p._id) !== (user?.id || user?._id));
              const isSelected = (conversation.id || conversation._id) === conversationId;
              const isOnline = onlineUsers.includes(participant?.id || participant?._id);

              return (
                <div
                  key={conversation.id || conversation._id}
                  onClick={() => setSearchParams({ conversation: conversation.id || conversation._id })}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <img
                        src={participant?.profilePicture || '/default-avatar.png'}
                        alt={participant?.username}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      {isOnline && (
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <h3 className="font-semibold text-gray-900 truncate">{participant?.username}</h3>
                        <span className="text-xs text-gray-500">
                          {conversation.lastMessage?.createdAt && new Date(conversation.lastMessage.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {conversation.lastMessage?.content?.text || 'No messages'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img
                  src={otherParticipant?.profilePicture || '/default-avatar.png'}
                  alt="User"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {otherParticipant?.username}
                  </h3>
                  {typing[selectedConversation.userId] ? (
                    <p className="text-sm text-green-600">{t('pages.messages.typing')}</p>
                  ) : (
                    <OnlineStatusBadge
                      isOnline={recipientOnlineStatus.isOnline}
                      lastSeen={recipientOnlineStatus.lastSeen}
                      showLastSeen={true}
                    />
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                {isGigger && (
                  <button
                    onClick={() => setShowChatOrderModal(true)}
                    className="flex items-center space-x-1 px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 shadow-md transform hover:scale-105 transition-all text-sm font-bold"
                  >
                    <PlusCircle className="w-4 h-4" />
                    <span>{t('pages.messages.create_order', 'Create Order')}</span>
                  </button>
                )}

                {isShipper && (
                  <button
                    onClick={() => setShowRequestModal(true)}
                    className="flex items-center space-x-1 px-4 py-2 border-2 border-blue-600 text-blue-600 rounded-full hover:bg-blue-50 transform hover:scale-105 transition-all text-sm font-bold"
                  >
                    <FileText className="w-4 h-4" />
                    <span>{t('pages.messages.request_order', 'Request Order')}</span>
                  </button>
                )}

                <button className="p-2 hover:bg-gray-100 rounded-full">
                  <MoreVertical className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <p>{t('pages.messages.no_messages')}</p>
                </div>
              ) : (
                messages.map((message) => {
                  const isSender = (message.sender?.id || message.sender?._id || message.senderId) === (user?.id || user?._id);
                  const isSystem = message.content?.type === 'system';

                  if (isSystem) {
                    return (
                      <div key={message._id || message.id} className="flex justify-center my-4">
                        <div className="bg-gray-200 text-gray-700 px-4 py-2 rounded-full text-sm flex items-center space-x-2">
                          <span>{message.content.text}</span>
                        </div>
                      </div>
                    );
                  }

                  const isRevoked = message.isRevoked || message.status === 'revoked';
                  const isVoice = message.contentType === 'voice' ||
                    message.content?.type === 'voice' ||
                    Boolean(message.audioUrl) ||
                    message.attachments?.[0]?.fileType?.startsWith('audio/');

                  return (
                    <div
                      key={message._id || message.id}
                      className={`flex ${isSender ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-xs lg:max-w-md ${isSender ? 'order-2' : 'order-1'}`}>
                        {!isSender && (
                          <img
                            src={message.sender?.profilePicture || '/default-avatar.png'}
                            alt={message.sender?.username || 'User'}
                            className="w-8 h-8 rounded-full mb-1"
                          />
                        )}

                        {/* Render Custom Cards, Voice Player, or Text Bubble */}
                        {isRevoked ? (
                          <RevokedMessageNotice
                            reason={message.revokedReason}
                            violations={message.violations}
                            isSender={isSender}
                          />
                        ) : isVoice ? (
                          <VoiceMessagePlayer
                            audioUrl={message.audioUrl || message.content?.url || message.attachments?.[0]?.fileUrl}
                            duration={message.content?.duration || message.duration}
                            isSender={isSender}
                            isRevoked={isRevoked}
                            revokedReason={message.revokedReason}
                            violations={message.violations}
                          />
                        ) : message.content?.type === 'custom_offer' && message.customOffer ? (
                          <CustomOfferCard
                            offer={message.customOffer}
                            currentUserId={user.id || user._id}
                            onUpdate={handleOfferUpdate}
                          />
                        ) : message.content?.type === 'custom_request' && message.customRequest ? (
                          <CustomRequestCard
                            request={message.customRequest}
                            currentUserId={user.id || user._id}
                            onUpdate={handleRequestUpdate}
                            onCounter={handleCounterRequest}
                          />
                        ) : message.content?.type === 'order_proposal' && message.chatOrder ? (
                          <ChatOrderCard
                            order={message.chatOrder}
                            currentUserId={user.id || user._id}
                            onAccept={handleChatOrderAccept}
                            onNegotiate={handleChatOrderNegotiate}
                            onReject={handleChatOrderReject}
                          />
                        ) : message.structuredData || message.content?.type === 'structured_update' ? (
                          <StructuredUpdateCard
                            data={message.structuredData || {}}
                            message={message}
                            isSender={isSender}
                          />
                        ) : (
                          <div
                            className={`px-4 py-2 rounded-lg ${isSender
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border text-gray-900'
                              }`}
                          >
                            {message.content?.text && (
                              <p className="break-words">{message.content.text}</p>
                            )}
                            {message.attachments?.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {message.attachments.map((file, idx) => (
                                  <a
                                    key={idx}
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center space-x-2 p-2 rounded ${isSender ? 'bg-blue-700' : 'bg-gray-100'
                                      }`}
                                  >
                                    <Paperclip className="w-4 h-4" />
                                    <span className="text-sm truncate">{file.filename}</span>
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        <p
                          className={`text-xs text-gray-500 mt-1 ${isSender ? 'text-right' : 'text-left'
                            }`}
                        >
                          {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="bg-white border-t p-4">
              {/* Anti-poaching warning — shown when user types restricted content */}
              {contentViolations.length > 0 && (
                <MessageWarning
                  violations={contentViolations}
                  severity="critical"
                  onDismiss={() => setContentViolations([])}
                />
              )}
              <div className="flex items-center space-x-2">
                {recorderMode === 'idle' && (
                  <>
                    <QuickReplyButton
                      onSelect={(text) => setMessageText(prev => prev ? `${prev} ${text}` : text)}
                      userRole={isGigger ? 'gigger' : 'shipper'}
                    />
                    <button
                      type="button"
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                      <Image className="w-5 h-5" />
                    </button>
                    <input
                      type="text"
                      value={messageText}
                      onChange={handleTyping}
                      placeholder={t('pages.messages.type_message')}
                      className={`flex-1 px-4 py-2 border rounded-full focus:outline-none focus:ring-2 ${
                        contentViolations.length > 0
                          ? 'border-red-400 focus:ring-red-400'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                    />
                    <button
                      type="button"
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                      <Smile className="w-5 h-5" />
                    </button>
                  </>
                )}

                <VoiceNoteRecorder
                  conversationId={conversationId}
                  recipientId={selectedConversation?.userId}
                  onVoiceSent={handleVoiceSent}
                  onStateChange={setRecorderMode}
                />

                {recorderMode === 'idle' && (
                  <button
                    type="submit"
                    disabled={!messageText.trim() || contentViolations.length > 0}
                    className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={contentViolations.length > 0 ? 'Remove prohibited content before sending' : 'Send message'}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                )}
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500 bg-gray-50">
            <div className="text-center p-8 max-w-md">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('pages.messages.welcome_title', 'Your Messages')}</h3>
              <p className="text-gray-600 mb-8">
                {t('pages.messages.select_conversation', 'Select a conversation from the sidebar to start chatting or checking your messages.')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedConversation && (
        <>
          <CustomOfferModal
            isOpen={showOfferModal}
            onClose={() => {
              setShowOfferModal(false);
              setActiveRequestForCounter(null);
            }}
            recipientId={selectedConversation.userId}
            conversationId={realConversationId}
            baseGig={activeRequestForCounter ? null : null}
            initialData={activeRequestForCounter}
            onSuccess={(offer) => {
              setShowOfferModal(false);
              setActiveRequestForCounter(null);
              refreshAfterCardSent();
            }}
          />

          <CustomRequestModal
            isOpen={showRequestModal}
            onClose={() => setShowRequestModal(false)}
            recipientId={selectedConversation.userId}
            conversationId={realConversationId}
            onSuccess={(request) => {
              setShowRequestModal(false);
              refreshAfterCardSent();
            }}
          />

          <ChatOrderModal
            isOpen={showChatOrderModal}
            onClose={() => {
              setShowChatOrderModal(false);
              setIsNegotiatingOrder(false);
              setActiveChatOrderForNegotiation(null);
            }}
            recipient={otherParticipant}
            conversationId={realConversationId}
            isNegotiating={isNegotiatingOrder}
            initialData={activeChatOrderForNegotiation}
            portfolioId={searchParams.get('portfolioId')}
            onSuccess={refreshAfterCardSent}
          />
        </>
      )}

      {showVerificationModal && (
        <VerificationRequiredModal
          isOpen
          onClose={() => setShowVerificationModal(false)}
          returnUrl={window.location.pathname + window.location.search}
        />
      )}
    </div>
  );
};

export default Messages;
